import React, { useEffect, useMemo, useState } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import Badge from "@components/Badge/Badge";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Select from "@components/Select/Select";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import NotifyDropdown from "@features/vessel-call/vessel-call-status/PortServices/components/NotifyDropdown";
import BunkeringExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/bunkering/BunkeringExtraFields";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import DeSlopingExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/de-sloping/DeSlopingExtraFields";
import { validateDeSlopingExtraFields } from "@features/vessel-call/vessel-call-status/PortServices/flow/de-sloping/utils";
import DebunkeringExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/debunkering/DebunkeringExtraFields";
import { validateDebunkeringExtraFields } from "@features/vessel-call/vessel-call-status/PortServices/flow/debunkering/utils";
import HSESlopsExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/SlopsExtraFields";
import SludgeExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/SludgeExtraFields";
import {
  isHSEWasteDisposalSlops,
  isHSEWasteDisposalSludge,
  validateHSESlopsDisposalExtraFields,
  validateSludgeDisposalExtraFields,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import {
  getDateMustNotBeInFutureError,
  getNotifyOptionRequiredMessage,
  isBunkering,
  isDebunkering,
  isDeSloping,
  isWasteDisposalGarbage,
  isWasteDisposalSlops,
  normalizeDate,
  notificationSchema,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import GarbageExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/GarbageExtraFields";
import SlopsExtraFields from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/SlopsExtraFields";
import {
  canRejectPortServiceFlowStep,
  canRejectWithoutActionPermission,
  getDefaultNotifyRolesForFlowStep,
  getPortServiceFlow,
  hasOperationsNotifyRole,
  validateBunkeringExtraFields,
  validateGarbageDisposalExtraFields,
  validateSlopsDisposalExtraFields,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import {
  getMainVessel,
  getSecondaryVessel,
} from "@features/vessel-call/vessel-call-status/PortServices/grid/utils";
import type { ChangeStatusDrawerInfo } from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import { useChangePortServiceStatus } from "@features/vessel-call/vessel-call-status/PortServices/hooks/useChangePortServiceStatus";
import {
  getPortServiceStatusInfo,
  NOTIFY_LIST,
  STATUS_KEYS,
  STATUS_MAP,
  USER_TYPES,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type FromToLocation } from "@services/vessel-call/types";
import { checkPermission } from "@utils/user-permissions";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MOVEMENT_SERVICE_STATUSES } from "../../MovementServices/flow/utils";
import LoadingAndOffloadingExtraFields from "./ship-chandling/LoadingAndOffloadingExtraFields";
import ShipChandlingLifeboatsExtraFields from "./ship-chandling/ShipChandlingLifeboatsExtraFields";
import {
  isLoadingAndOffloadingType,
  isRestrictedLifeboatsMDUser,
  isShipChandlingLifeboatsWorkflowType,
  mapLoadingAndOffloadingResourcesToShipChandlingResources,
  validateLoadingAndOffloadingExtraFields,
  validateShipChandlingLifeboatsExtraFields,
} from "./ship-chandling/utils";
import ShipToShipExtraFields from "./ship-to-ship/ShipToShipExtraFields";
import { isShipToShipType, validateShipToShipExtraFields } from "./ship-to-ship/utils";

const LANG_BASE = "vessel-call.port-services";

interface CancelServiceDrawerProps {
  drawerInfo: ChangeStatusDrawerInfo | null;
  onClose: () => void;
  setShouldRefetch: (value: boolean) => void;
}

const asDateOrNull = (value: unknown): Date | null => {
  if (value == null || value === "") return null;

  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getAtcMinDateFromAts = (ats: string | Date | null | undefined): Date | undefined => {
  if (!ats) return undefined;

  const minDate = new Date(ats);
  if (Number.isNaN(minDate.getTime())) return undefined;

  minDate.setSeconds(0, 0);
  minDate.setMinutes(minDate.getMinutes() + 1);

  return minDate;
};

const getFallbackStatusKeysForUser = ({ userType }: { userType: string }) => {
  if (userType === USER_TYPES.SHIPPING_AGENT) {
    return [STATUS_KEYS.STARTED, STATUS_KEYS.CLOSED];
  }

  if (userType === USER_TYPES.MARINE_DEPARTMENT)
    return [STATUS_KEYS.MD_APPROVED, STATUS_KEYS.REJECTED];

  if (userType === USER_TYPES.OPERATIONS)
    return [STATUS_KEYS.OPERATIONS_APPROVED, STATUS_KEYS.REJECTED];

  if (userType === USER_TYPES.HSE) return [STATUS_KEYS.HSE_APPROVED, STATUS_KEYS.REJECTED];

  return [];
};

const NEW_STATUSES_WITH_DATE_FILEDS = ["STARTED", "CLOSED"];
const isDateTimePickerVisible = (newStatus: string) =>
  NEW_STATUSES_WITH_DATE_FILEDS.includes(newStatus);

const changeStatusSchema = (t: TFunction) =>
  z
    .object({
      newStatus: z.string().min(1, { message: "Please select a new status" }),
      ats: z.date().nullable().optional(),
      atc: z.date().nullable().optional(),
    })
    .merge(notificationSchema)
    .superRefine((val, ctx) => {
      if (val.newStatus === STATUS_KEYS.STARTED && !val.ats) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ats"],
          message: t("form-validation.required", { label: "ATS" }),
        });
      }
      const atsError =
        val.newStatus === STATUS_KEYS.STARTED
          ? getDateMustNotBeInFutureError(val.ats, "ATS")
          : undefined;

      if (atsError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ats"],
          message: atsError,
        });
      }

      if (val.newStatus === STATUS_KEYS.CLOSED && !val.atc) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: t("form-validation.required", { label: "ATC" }),
        });
      }

      const atcError =
        val.newStatus === STATUS_KEYS.CLOSED
          ? getDateMustNotBeInFutureError(val.atc, "ATC")
          : undefined;

      if (atcError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: atcError,
        });
      }

      if (val.newStatus === STATUS_KEYS.CLOSED && val.atc && val.ats && val.atc <= val.ats) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: t("form-validation.must-be-after", { subject: "ATC", object: "ATS" }),
        });
      }
    });

const getNormalizedIssueMessage = (pathKey: string | undefined, issueMessage: string) => {
  if (
    pathKey === "notificationOption" &&
    issueMessage === "Array must contain at least 1 element(s)"
  ) {
    return getNotifyOptionRequiredMessage();
  }

  return issueMessage;
};

const DateTimePicker = ({
  newStatus,
  ats,
  atc,
  setAts,
  setAtc,
  error,
}: {
  newStatus: string;
  ats: string | Date | null;
  atc: string | Date | null;
  setAts: (arg: Date | null) => void;
  setAtc: (arg: Date | null) => void;
  error?: string;
}) => {
  let label: "ATS" | "ATC";
  let handleChange: (arg: Date | null) => void;
  let value: string | Date | null;
  const atcMinDate = getAtcMinDateFromAts(ats);

  if (newStatus === STATUS_KEYS.STARTED) {
    label = "ATS";
    handleChange = setAts;
    value = ats;
  } else {
    label = "ATC";
    handleChange = setAtc;
    value = atc;
  }

  const resolvedMinDate = newStatus === STATUS_KEYS.CLOSED ? atcMinDate : undefined;

  return (
    <div className="flex flex-col gap-2 mt-6">
      <CustomFormLabel data-testid={`change-status-${label}-label`}>{label}</CustomFormLabel>
      <DatePicker
        hasError={!!error}
        value={asDateOrNull(value)}
        onChange={(date) => handleChange(date)}
        testId={`${label}-date-input`}
        timePicker={true}
        combinedInput={true}
        dateConstraint="not-future"
        minDate={resolvedMinDate}
      />
      {error && <FormHelperText sx={{ color: "error.main", m: 0 }}>{error}</FormHelperText>}
    </div>
  );
};

const ChangeStatusServiceDrawer: React.FC<CancelServiceDrawerProps> = ({
  drawerInfo,
  onClose,
  setShouldRefetch,
}) => {
  const { t } = useTranslation();
  const [notifyOption, setNotifyOption] = useState<string[]>([]);
  const [newStatusOption, setNewStatusOption] = useState("");
  const [ats, setAts] = useState<Date | null>(null);
  const [atc, setAtc] = useState<Date | null>(null);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const {
    ats: flowAts,
    etc: flowEtc,
    atc: flowAtc,
    ets: flowEts,
    garbageDisposalDocument,
    billOfLading,
    cargoType,
    operationType,
    slopsDisposalDocuments,
    actualNumberOfSkips,
    actualQuantity,
    images,
    specialInstructions,
    notes,
    chargedParty,
    resourcesAndEquipment,
    serviceReportAttachment,
    attachment,
    wasteResiduesForDischargeFacility,
    errors: flowErrors,
    setErrors: setFlowErrors,
    rejectionReason,
    internalNotes,
    internalAttachment,
    externalNotes,
    externalAttachment,
    rejectionAttachment,
    numberOfPackagesHandled,
    spares,
    incidents,
    resources,
  } = usePortServicesFlow();

  const isGarbageFlow = isWasteDisposalGarbage(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
  );
  const isSludgeFlow = isHSEWasteDisposalSludge(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
    drawerInfo?.serviceSubType ?? "",
  );
  const isHSESlopsFlow = isHSEWasteDisposalSlops(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
    drawerInfo?.serviceSubType ?? "",
  );

  const isSlopsFlow = isWasteDisposalSlops(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
  );
  const isShipToShipFlow = isShipToShipType(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
  );
  const isShipChandlingFlow = isShipChandlingLifeboatsWorkflowType(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
  );
  const isBunkeringFlow = isBunkering(drawerInfo?.serviceCategory ?? "");
  const isDebunkeringFlow = isDebunkering(drawerInfo?.serviceCategory ?? "");
  const isDeSlopingFlow = isDeSloping(drawerInfo?.serviceCategory ?? "");
  const isLoadingAndOffloadingFlow = isLoadingAndOffloadingType(
    drawerInfo?.serviceCategory ?? "",
    drawerInfo?.serviceType ?? "",
  );
  const isManagedFlow =
    isGarbageFlow ||
    isSludgeFlow ||
    isSlopsFlow ||
    isBunkeringFlow ||
    isShipToShipFlow ||
    isShipChandlingFlow ||
    isDebunkeringFlow ||
    isDeSlopingFlow ||
    isHSESlopsFlow ||
    isLoadingAndOffloadingFlow;
  const isTwoColumnClosedLayout =
    newStatusOption === STATUS_KEYS.CLOSED && (isShipChandlingFlow || isLoadingAndOffloadingFlow);

  const { options: locations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );
  const mainMovementStatus = getMainVessel(drawerInfo?.linkedVesselCalls)?.associatedMovementStatus;
  const secondaryMovementStatus = getSecondaryVessel(
    drawerInfo?.linkedVesselCalls,
  )?.associatedMovementStatus;

  const CLOSED_OR_COMPLETED = [
    MOVEMENT_SERVICE_STATUSES.CLOSED,
    MOVEMENT_SERVICE_STATUSES.COMPLETED,
  ];

  const isLinkedVesselCallsError =
    isShipToShipFlow || isBunkeringFlow
      ? newStatusOption === STATUS_KEYS.STARTED &&
        !(mainMovementStatus && CLOSED_OR_COMPLETED.includes(mainMovementStatus)) &&
        !(secondaryMovementStatus && CLOSED_OR_COMPLETED.includes(secondaryMovementStatus))
      : null;

  const isBerthLocation = useMemo(() => {
    const locationCode = drawerInfo?.location;
    if (!locationCode) return false;
    return locations.some(
      (item) => item.fromToLocationCode === locationCode && item.location === "BERTH",
    );
  }, [drawerInfo?.location, locations]);
  const { isOperationsUser } = useAuthStore();
  const [errors, setErrors] = useState<{
    notificationOption?: string;
    ats?: string;
    atc?: string;
    newStatus?: string;
    nocDocument?: string;
  }>({});

  const notificationError = isManagedFlow
    ? flowErrors.notificationOption
    : errors.notificationOption;

  //needsOperationsApproval flag is used on BE for other flows for submission/approval flow (e.g Garbage)
  const bunkeringServices = isBunkeringFlow || isDebunkeringFlow || isDeSlopingFlow;

  const needsOperationsApproval =
    newStatusOption === STATUS_KEYS.MD_APPROVED && bunkeringServices
      ? hasOperationsNotifyRole(notifyOption)
      : newStatusOption === STATUS_KEYS.OPERATIONS_APPROVED && !bunkeringServices
        ? false
        : newStatusOption === STATUS_KEYS.REJECTED && isOperationsUser
          ? true
          : drawerInfo?.needsOperationsApproval;

  const shipChandlingResourcesAndEquipment =
    resources?.length && isShipChandlingFlow
      ? mapLoadingAndOffloadingResourcesToShipChandlingResources(resources)
      : resourcesAndEquipment;

  const { onSubmit } = useChangePortServiceStatus({
    status: newStatusOption,
    serviceCategory: drawerInfo?.serviceCategory,
    ats: (ats || flowAts) ?? (drawerInfo?.ats || null),
    atc: (atc || flowAtc) ?? (drawerInfo?.atc || null),
    etc: flowEtc ?? (drawerInfo?.etc || null),
    ets: flowEts ?? (drawerInfo?.ets || null),
    numberOfSkips: actualNumberOfSkips ?? (drawerInfo?.numberOfSkips || null),
    quantity: actualQuantity ?? drawerInfo?.quantity ?? null,
    numberOfPackagesHandled:
      numberOfPackagesHandled != null
        ? numberOfPackagesHandled
        : drawerInfo?.numberOfPackagesHandled != null
          ? Number(drawerInfo.numberOfPackagesHandled)
          : null,
    chargedParty: chargedParty || drawerInfo?.chargedParty || null,
    spares: spares != null ? spares : drawerInfo?.spares != null ? Number(drawerInfo.spares) : null,
    incidents: incidents || drawerInfo?.incidents || null,
    resourcesAndEquipment: shipChandlingResourcesAndEquipment,
    notifyList: notifyOption,
    portServiceId: drawerInfo?.id || "",
    internalNotes: internalNotes ?? (drawerInfo?.internalNotes || null),
    externalNotes: externalNotes ?? (drawerInfo?.externalNotes || null),
    specialInstructions: specialInstructions ?? (drawerInfo?.specialInstructions || null),
    wasteResiduesForDischargeFacility:
      wasteResiduesForDischargeFacility ?? (drawerInfo?.wasteResiduesForDischargeFacility || null),
    rejectionReason: rejectionReason ?? (drawerInfo?.rejectionReason || null),
    notes: notes ?? (drawerInfo?.notes || null),
    resources: isShipChandlingFlow ? undefined : resources,
    garbageDisposalDocument,
    billOfLading,
    cargoType,
    operationType,
    attachment,
    images,
    slopsDisposalDocuments,
    internalAttachment,
    externalAttachment,
    rejectionAttachment,
    serviceReportAttachment,
    needsOperationsApproval,
    useUsageTimeKeysForResources: isShipChandlingFlow,
    setShouldRefetch: setShouldRefetch,
    onClose: onClose,
  });

  const { user, userActionPermissions, userType } = useAuthStore();
  const currentUserType = userType ?? "Other";
  const currentUserRoles = user?.roles ?? [];
  const effectiveCurrentUserType = isRestrictedLifeboatsMDUser({
    category: drawerInfo?.serviceCategory || "",
    type: drawerInfo?.serviceType || "",
    currentUserType,
    currentUserRoles,
  })
    ? "Other"
    : currentUserType;
  const currentStatus = drawerInfo?.currentStatus ?? "";
  type StatusOption = {
    label: string;
    badgeType: string;
    canBeSelected: boolean;
    actionPermission: string | null;
    extraFields?: string[];
  };
  let newStatusOptions: { label: string; value: string }[] = [];
  let flowStatusesList: Record<string, StatusOption> = {};
  const flow = getPortServiceFlow(
    drawerInfo?.serviceCategory || "",
    drawerInfo?.serviceType || "",
    drawerInfo?.serviceSubType || "",
    drawerInfo?.needsOperationsApproval,
  );

  const currentFlowStep = flow?.[currentStatus] ?? null;
  if (flow && currentFlowStep) {
    flowStatusesList = Object.fromEntries(
      Object.entries(currentFlowStep.statusesList).map(([key, status]) => {
        const canSelectReject = canRejectPortServiceFlowStep(
          currentFlowStep,
          effectiveCurrentUserType,
        );
        return [
          key,
          {
            ...status,
            canBeSelected:
              key === STATUS_KEYS.REJECTED
                ? canSelectReject
                : currentFlowStep.canApprove.includes(effectiveCurrentUserType),
          },
        ];
      }),
    );
  }

  const userTypeBasedStatusList = getFallbackStatusKeysForUser({
    userType: effectiveCurrentUserType,
  });

  const statusList: Record<string, StatusOption> = flow
    ? flowStatusesList
    : Object.fromEntries(
        userTypeBasedStatusList
          .filter((key) => STATUS_MAP[key])
          .map((key) => [key, STATUS_MAP[key]]),
      );

  newStatusOptions = Object.keys(statusList)
    .filter((key) => {
      const shouldSkipPermission =
        key === STATUS_KEYS.REJECTED && canRejectWithoutActionPermission(currentFlowStep);

      return (
        statusList[key].canBeSelected &&
        (shouldSkipPermission ||
          checkPermission(userActionPermissions, statusList[key].actionPermission!))
      );
    })
    .map((key) => ({
      label: getPortServiceStatusInfo(key, drawerInfo?.serviceCategory).label,
      value: key,
    }));

  useEffect(() => {
    if (newStatusOption && (ats || atc)) {
      setAts(null);
      setAtc(null);
    }

    setErrors((prev) => ({ ...prev, nocDocument: undefined }));

    if (!flow || !drawerInfo) return;

    if (newStatusOption === STATUS_KEYS.REJECTED) {
      setNotifyOption(["Shipping Agent"]);
    } else {
      setNotifyOption(getDefaultNotifyRolesForFlowStep(currentFlowStep, newStatusOption) || []);
    }
  }, [newStatusOption]);

  useEffect(() => {
    if (!flow || !drawerInfo) return;

    const initialStatusOption = newStatusOptions[0]?.value ?? "";

    setNewStatusOption(initialStatusOption);
    setNotifyOption(getDefaultNotifyRolesForFlowStep(currentFlowStep, initialStatusOption) || []);
  }, [drawerInfo]);

  if (!drawerInfo) return null;

  const changeStatusDialogProps = {
    title: t(`${LANG_BASE}.change-status-dialog-title`),
    content: t(`${LANG_BASE}.change-status-dialog-message`, {
      currentStatus: getPortServiceStatusInfo(
        drawerInfo?.currentStatus,
        drawerInfo?.serviceCategory,
      ).label,
      newStatus: STATUS_MAP[newStatusOption]
        ? getPortServiceStatusInfo(newStatusOption, drawerInfo?.serviceCategory).label
        : "",
    }),
    confirmButtonText: t("common.confirm"),
    cancelButtonText: t("common.close"),
    isInsideDrawer: true,
    handleCloseDialog: () => {
      setChangeStatusDialogOpen(false);
    },
    confirmAction: () => {
      onSubmit();
      setChangeStatusDialogOpen(false);
      onClose();
    },
  };

  const handleSubmit = async () => {
    if (isLinkedVesselCallsError) return;
    let hasNocDocumentError = false;
    if ((isHSESlopsFlow || isSludgeFlow) && newStatusOption === STATUS_KEYS.STARTED) {
      const nocDocs = drawerInfo?.documents?.filter((doc) => doc.type === "NOC") ?? [];
      const allNocExpired =
        nocDocs.length > 0 &&
        nocDocs.every(
          (doc) => doc.expirationDate != null && new Date(doc.expirationDate) < new Date(),
        );
      if (allNocExpired) {
        setErrors((prev) => ({
          ...prev,
          nocDocument: t(`${LANG_BASE}.noc-required-to-start`),
        }));
        hasNocDocumentError = true;
      }
    }
    if (newStatusOption === STATUS_KEYS.REJECTED && !notifyOption.includes("Shipping Agent")) {
      const shippingAgentError = t(`${LANG_BASE}.fields.missing-notify-roles`, {
        count: 1,
        roles: "Shipping Agent",
      });
      if (isManagedFlow) {
        setFlowErrors((prev) => ({ ...prev, notificationOption: shippingAgentError }));
      } else {
        setErrors((prev) => ({ ...prev, notificationOption: shippingAgentError }));
      }
      return;
    }
    // derive the values we validate (use ATS from drawerInfo for ATC validation)
    const atsValue = ats ?? normalizeDate(drawerInfo?.ats ?? null);
    const atcValue = atc;
    const missingDefaultRoles = getDefaultNotifyRolesForFlowStep(
      currentFlowStep,
      newStatusOption,
      currentFlowStep?.shouldAppendNotifyRoles,
    ).filter((role) => !notifyOption.includes(role));
    const shouldRequireDefaultNotifyRoles =
      newStatusOption !== STATUS_KEYS.REJECTED ||
      ((isDebunkeringFlow || isDeSlopingFlow) && !!currentFlowStep?.shouldAppendNotifyRoles);
    const defaultRolesError =
      shouldRequireDefaultNotifyRoles && missingDefaultRoles.length > 0
        ? isLoadingAndOffloadingFlow || isShipChandlingFlow
          ? t(`${LANG_BASE}.fields.missing-notify-roles`, {
              roles: missingDefaultRoles.join(", "),
            })
          : t(`${LANG_BASE}.fields.error-message-notify-roles`, {
              count: missingDefaultRoles.length,
              roles: missingDefaultRoles.join(", "),
            })
        : null;
    let result;
    if (isGarbageFlow) {
      result = validateGarbageDisposalExtraFields({
        newStatusOption,
        notifyOption,
        actualNumberOfSkips,
        internalNotes,
        externalNotes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        actualQuantity,
        garbageDisposalDocument,
        isBerth: isBerthLocation,
      });
    } else if (isSludgeFlow) {
      result = validateSludgeDisposalExtraFields({
        newStatusOption,
        notifyOption,
        specialInstructions,
        wasteResiduesForDischargeFacility,
        actualNumberOfSkips,
        notes,
        internalNotes,
        externalNotes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        images,
        serviceReportAttachment,
        attachment,
      });
    } else if (isHSESlopsFlow) {
      result = validateHSESlopsDisposalExtraFields({
        newStatusOption,
        notifyOption,
        specialInstructions,
        wasteResiduesForDischargeFacility,
        notes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        slopsDisposalDocuments,
        serviceReportAttachment,
        attachment,
      });
    } else if (isSlopsFlow) {
      result = validateSlopsDisposalExtraFields({
        newStatusOption,
        notifyOption,
        internalNotes,
        externalNotes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        flowEts,
        actualQuantity,
        actualNumberOfSkips,
        slopsDisposalDocuments,
        serviceReportAttachment,
        wasteResiduesForDischargeFacility,
      });
    } else if (isBunkeringFlow) {
      result = validateBunkeringExtraFields({
        newStatusOption,
        notifyOption,
        internalNotes,
        externalNotes,
        rejectionReason,
        notes,
        attachment,
        flowAts,
        flowEtc,
        flowAtc,
        actualQuantity,
        serviceReportAttachment,
      });
    } else if (isShipToShipFlow) {
      result = validateShipToShipExtraFields({
        newStatusOption,
        notifyOption,
        internalNotes,
        externalNotes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        billOfLading,
        cargoType,
        operationType,
        actualQuantity,
      });
    } else if (isShipChandlingFlow) {
      result = validateShipChandlingLifeboatsExtraFields({
        newStatusOption,
        notifyOption,
        chargedParty,
        notes,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        numberOfPackagesHandled,
        spares,
        incidents,
        resources,
        attachment,
        internalNotes,
        externalNotes,
      });
    } else if (isDebunkeringFlow) {
      result = validateDebunkeringExtraFields({
        newStatusOption,
        notifyOption,
        internalNotes,
        externalNotes,
        rejectionReason,
        notes,
        attachment,
        flowAts,
        flowEtc,
        flowAtc,
        actualQuantity,
        serviceReportAttachment,
      });
    } else if (isDeSlopingFlow) {
      result = validateDeSlopingExtraFields({
        newStatusOption,
        notifyOption,
        internalNotes,
        externalNotes,
        rejectionReason,
        notes,
        attachment,
        flowAts,
        flowEtc,
        flowAtc,
        actualQuantity,
        serviceReportAttachment,
      });
    } else if (isLoadingAndOffloadingFlow) {
      result = validateLoadingAndOffloadingExtraFields({
        newStatusOption,
        notifyOption,
        notes,
        attachment,
        rejectionReason,
        flowAts,
        flowEtc,
        flowAtc,
        numberOfPackagesHandled,
        spares,
        incidents,
        resources,
      });
    } else {
      result = changeStatusSchema(t).safeParse({
        newStatus: newStatusOption, // or require selection
        notificationOption: notifyOption,
        ats: atsValue,
        atc: atcValue,
      });
    }
    if (!result?.success) {
      if (isManagedFlow) {
        const flowFieldErrors: typeof flowErrors = {};
        if (result && result.error && Array.isArray(result.error.issues)) {
          for (const issue of result.error.issues) {
            const pathKey = issue.path[0] as keyof typeof flowFieldErrors;
            if (!pathKey) continue;
            const normalizedPathKey =
              isShipChandlingFlow && pathKey === "resourcesAndEquipment" ? "resources" : pathKey;
            flowFieldErrors[normalizedPathKey] = getNormalizedIssueMessage(
              normalizedPathKey,
              issue.message,
            ) as any;
          }
        }

        if (
          (isSludgeFlow || isHSESlopsFlow) &&
          newStatusOption === STATUS_KEYS.CLOSED &&
          !serviceReportAttachment?.documentUrl?.trim()
        ) {
          flowFieldErrors.serviceReportAttachment = t("form-validation.required", {
            label: t("vessel-call.port-services.fields.service-report"),
          });
        }

        // override notification error with more specific default roles error if applicable
        if (defaultRolesError) flowFieldErrors.notificationOption = defaultRolesError;
        // preserve cross-field errors set by ATCField / ETCField
        if (flowErrors.atc) flowFieldErrors.atc = flowErrors.atc;
        if (flowErrors.etc) flowFieldErrors.etc = flowErrors.etc;
        setFlowErrors(flowFieldErrors);
      } else {
        const fieldErrors: typeof errors = {};
        if (result && result.error && Array.isArray(result.error.issues)) {
          for (const issue of result.error.issues) {
            const pathKey = issue.path[0] as keyof typeof fieldErrors;
            if (!pathKey) continue;
            fieldErrors[pathKey] = getNormalizedIssueMessage(pathKey, issue.message);
          }
        }
        setErrors(fieldErrors);
      }
      return;
    }
    // block submission if cross-field errors remain (e.g. ATC <= ATS, ETC <= ATS) or missing required notify roles
    if (isManagedFlow && (flowErrors.atc || flowErrors.etc || defaultRolesError)) {
      if (defaultRolesError) {
        setFlowErrors((prev) => ({ ...prev, notificationOption: defaultRolesError }));
      }
      return;
    }
    // clear previous errors and call original submit
    if (isManagedFlow) {
      setFlowErrors({});
    } else {
      setErrors({});
    }
    if (hasNocDocumentError) return;
    setChangeStatusDialogOpen(true);
  };

  return (
    <div data-testid="change-port-service-status-drawer">
      <SideDrawer
        isOpen={!!drawerInfo?.isOpen}
        title={t(`${LANG_BASE}.change-status-title`)}
        cancelLabel={t("common.cancel")}
        onClose={onClose}
        submitLabel={t(`${LANG_BASE}.change-status-button`)}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <div className={isTwoColumnClosedLayout ? "min-w-3xl max-w-3xl" : "min-w-sm max-w-sm"}>
            <Typography
              variant="body1"
              data-testid="port-service-form-title"
              sx={{ color: "text.primary", pb: 1 }}
            >
              {t(`${LANG_BASE}.current-status`)}
            </Typography>
            <Badge
              label={
                getPortServiceStatusInfo(drawerInfo.currentStatus, drawerInfo?.serviceCategory)
                  .label
              }
              type={
                getPortServiceStatusInfo(drawerInfo.currentStatus, drawerInfo?.serviceCategory)
                  .badgeType
              }
            />
            <div
              className={
                isTwoColumnClosedLayout
                  ? "mt-6 grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2"
                  : ""
              }
            >
              <div
                className={isTwoColumnClosedLayout ? "md:col-start-1" : "flex flex-col gap-2 my-6"}
              >
                <CustomFormLabel data-testid="statusLabelId">
                  {t(`${LANG_BASE}.new-status`)}
                </CustomFormLabel>
                <Select
                  options={newStatusOptions}
                  value={newStatusOption}
                  onChange={(val) => {
                    setNewStatusOption(val);
                    setErrors((prev) => ({ ...prev, newStatus: undefined }));
                    if (isManagedFlow) {
                      setFlowErrors((prev) => ({ ...prev, notificationOption: undefined }));
                    } else {
                      setErrors((prev) => ({
                        ...prev,
                        newStatus: undefined,
                        notificationOption: undefined,
                      }));
                    }
                  }}
                  testId={`port-serives-preview-status-select`}
                  placeholder={t(`${LANG_BASE}.new-status`)}
                  style={{ width: "100%", marginTop: "4px" }}
                />
                {errors.newStatus && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.newStatus}
                  </FormHelperText>
                )}
                {isLinkedVesselCallsError && (
                  <Alert
                    type="warning"
                    style={{ backgroundColor: "white", border: "none" }}
                    className="px-0! text-left"
                  >
                    {t(`${LANG_BASE}.linked-vessel-calls-not-closed`)}
                  </Alert>
                )}
                {errors.nocDocument && (
                  <Alert
                    type="warning"
                    style={{ backgroundColor: "white", border: "none" }}
                    className="px-0! text-left"
                  >
                    {errors.nocDocument}
                  </Alert>
                )}
              </div>
              <div className={isTwoColumnClosedLayout ? "md:col-start-1" : "flex flex-col gap-2"}>
                <CustomFormLabel data-testid="change-status-notify-label">
                  {t(`${LANG_BASE}.notify`)}
                </CustomFormLabel>
                <NotifyDropdown
                  options={NOTIFY_LIST}
                  option={notifyOption}
                  setOption={(opt) => {
                    setNotifyOption(opt);
                    if (isManagedFlow) {
                      setFlowErrors((prev) => ({ ...prev, notificationOption: undefined }));
                    } else {
                      setErrors((prev) => ({ ...prev, notificationOption: undefined }));
                    }
                  }}
                />
                {notificationError && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {notificationError}
                  </FormHelperText>
                )}
              </div>

              {!flow && isDateTimePickerVisible(newStatusOption) && (
                <div className={isTwoColumnClosedLayout ? "md:col-start-1" : "flex flex-col gap-2"}>
                  <DateTimePicker
                    newStatus={newStatusOption}
                    ats={ats ?? (drawerInfo?.ats || null)}
                    atc={atc ?? (drawerInfo?.atc || null)}
                    setAts={(d) => {
                      setAts(d);
                      setErrors((prev) => ({ ...prev, ats: undefined }));
                    }}
                    setAtc={(d) => {
                      setAtc(d);
                      setErrors((prev) => ({ ...prev, atc: undefined }));
                    }}
                    error={newStatusOption === STATUS_KEYS.STARTED ? errors.ats : errors.atc}
                  />
                </div>
              )}
              {flow && isGarbageFlow && (
                <GarbageExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isSludgeFlow && (
                <SludgeExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isSlopsFlow && (
                <SlopsExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isBunkeringFlow && (
                <BunkeringExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}

              {flow && isShipToShipFlow && (
                <ShipToShipExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isHSESlopsFlow && (
                <HSESlopsExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isDebunkeringFlow && (
                <DebunkeringExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}

              {flow && isDeSlopingFlow && (
                <DeSlopingExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isLoadingAndOffloadingFlow && (
                <LoadingAndOffloadingExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
              {flow && isShipChandlingFlow && (
                <ShipChandlingLifeboatsExtraFields
                  currentFlowStep={currentFlowStep}
                  selectedStatus={newStatusOption}
                  serviceDetails={drawerInfo}
                />
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
      <GenericDialog open={changeStatusDialogOpen} {...changeStatusDialogProps} />
    </div>
  );
};

export default ChangeStatusServiceDrawer;
